import React from "react";
import {
  Box,
  Tabs,
  Tab,
  Card,
  Avatar,
  Stack,
  Typography,
  Chip,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  Email as EmailIcon,
  Phone as PhoneIcon,
  Person as PersonIcon,
  Archive as ArchiveIcon,
  Circle as CircleIcon,
  LocationOn as LocationIcon,
  Wc as GenderIcon,
  Cake as CakeIcon,
  Work as WorkIcon,
  Assignment as AssignmentIcon,
} from "@mui/icons-material";
import Loader from "../Loader/Loader";

const TableWithNav = ({
  data,
  test,
  tabHeader,
  handleArchive,
  clickedId,
  isLoading,
}) => {
  const [activeTab, setActiveTab] = React.useState(0);

  if (isLoading) {
    return <Loader />;
  }

  const getInitials = (firstName, lastName) => {
    if (!firstName && !lastName) return "?";
    return `${firstName?.[0] || ""}${lastName?.[0] || ""}`.toUpperCase();
  };

  const getStatusColor = (status) => {
    const statusMap = {
      pending: "warning",
      active: "success",
      inactive: "error",
      archived: "default",
    };
    return statusMap[status?.toLowerCase()] || "default";
  };

  const getStatusLabel = (status) => {
    return status?.toUpperCase() || "UNKNOWN";
  };

  if (!clickedId) {
    return (
      <Box
        sx={{
          height: "100%",
          minHeight: "60vh",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          border: "2px dashed #E2E8F0",
          borderRadius: 2,
          p: 4,
          gap: 1.5,
        }}
      >
        <PersonIcon sx={{ fontSize: 56, color: "#CBD5E1" }} />
        <Typography variant="h6" fontWeight={500} color="#475467">
          Select a Candidate
        </Typography>
        <Typography variant="body2" color="#94A3B8" textAlign="center">
          Click on any candidate from the list to view their details
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", height: "100%", display: "flex", flexDirection: "column", gap: 2 }}>
      <Card
        elevation={0}
        sx={{
          borderRadius: 2,
          border: "1px solid #E2E8F0",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            p: 2.5,
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar
              src={data?.profile_photo}
              alt={`${data?.first_name} ${data?.last_name}`}
              sx={{
                width: 48,
                height: 48,
                bgcolor: "primary.main",
                border: "2px solid white",
                boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
              }}
            >
              {!data?.profile_photo && (
                <PersonIcon sx={{ fontSize: 24, color: "white" }} />
              )}
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight={600} color="#1A1A2E">
                {data?.first_name} {data?.last_name}
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Chip
                  label={getStatusLabel(data?.status)}
                  size="small"
                  color={getStatusColor(data?.status)}
                  sx={{ height: 20, fontSize: "0.6rem", fontWeight: 600 }}
                />
                <Chip
                  label={`Interview: ${data?.interview_status || "N/A"}`}
                  size="small"
                  color={data?.interview_status === "True" ? "success" : "default"}
                  sx={{ height: 20, fontSize: "0.6rem", fontWeight: 600 }}
                />
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <EmailIcon sx={{ fontSize: 14, color: "#94A3B8" }} />
                  <Typography variant="caption" color="#6B7280">
                    {data?.primary_email || "N/A"}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <PhoneIcon sx={{ fontSize: 14, color: "#94A3B8" }} />
                  <Typography variant="caption" color="#6B7280">
                    {data?.mobile_phone || "N/A"}
                  </Typography>
                </Box>
              </Stack>
            </Box>
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center">
            <Chip
              icon={<CircleIcon sx={{ fontSize: 8 }} />}
              label={data?.is_my_zones ? "My Zone" : "All Zone"}
              size="small"
              color={data?.is_my_zones ? "primary" : "default"}
              variant="outlined"
              sx={{ height: 22, fontSize: "0.6rem" }}
            />
            <Tooltip title="Archive Candidate" arrow>
              <IconButton
                size="small"
                onClick={() => handleArchive?.(data.id)}
                sx={{ color: "#94A3B8", "&:hover": { color: "#EF4444" } }}
              >
                <ArchiveIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>

        <Box
          sx={{
            px: 2.5,
            py: 1.5,
            borderTop: "1px solid #E2E8F0",
            display: "flex",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          {data?.address_1 && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <LocationIcon sx={{ fontSize: 14, color: "#94A3B8" }} />
              <Typography variant="caption" color="#6B7280">
                {data?.address_1}
              </Typography>
            </Box>
          )}
          {data?.gender && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <GenderIcon sx={{ fontSize: 14, color: "#94A3B8" }} />
              <Typography variant="caption" color="#6B7280">
                {data?.gender}
              </Typography>
            </Box>
          )}
          {data?.birthdate && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <CakeIcon sx={{ fontSize: 14, color: "#94A3B8" }} />
              <Typography variant="caption" color="#6B7280">
                {new Date(data?.birthdate).toLocaleDateString()}
              </Typography>
            </Box>
          )}
          {data?.current_employer && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <WorkIcon sx={{ fontSize: 14, color: "#94A3B8" }} />
              <Typography variant="caption" color="#6B7280">
                {data?.current_employer}
              </Typography>
            </Box>
          )}
          {data?.position_held && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <AssignmentIcon sx={{ fontSize: 14, color: "#94A3B8" }} />
              <Typography variant="caption" color="#6B7280">
                {data?.position_held}
              </Typography>
            </Box>
          )}
        </Box>
      </Card>

      <Card
        elevation={0}
        sx={{
          flex: 1,
          borderRadius: 2,
          border: "1px solid #E2E8F0",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          backgroundColor: "transparent",
        }}
      >
        <Tabs
          value={activeTab}
          onChange={(event, newValue) => setActiveTab(newValue)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            minHeight: 44,
            borderBottom: "1px solid #E2E8F0",
            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: 500,
              fontSize: "0.75rem",
              minHeight: 44,
              py: 0.75,
              px: 1.5,
              color: "#64748B",
              "&.Mui-selected": {
                color: "#2469F0",
              },
            },
            "& .MuiTabs-indicator": {
              backgroundColor: "#2469F0",
              height: 2,
            },
          }}
        >
          {tabHeader.map((tab, index) => (
            <Tab
              key={tab.id}
              label={tab.title}
              disabled={tab.disabled}
              icon={tab.icon}
              iconPosition="start"
              sx={{
                "&.Mui-disabled": { opacity: 0.4 },
              }}
            />
          ))}
        </Tabs>

        <Box
          sx={{
            flex: 1,
            overflow: "auto",
            p: 2.5,
          }}
        >
          {tabHeader[activeTab]?.component({ clickedId })}
        </Box>
      </Card>
    </Box>
  );
};

export default TableWithNav;